import type { OptionProps, SelectProps } from './const';
import { Trigger } from '@arco-design/web-react';
import { cn } from '@utils';
import { IconAngleBottom } from '@wegic/icon';
import { Children, forwardRef, isValidElement, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Dropdown } from './Dropdown';
import { Option } from './Option';
import './index.less';

function Select(props: SelectProps, ref: React.Ref<HTMLDivElement>) {
  const {
    value = '',
    defaultValue = '',
    placeholder = 'Select an option',
    children,
    onChange,
    disabled,
    className,
    style,
    dropdownClassName,
    dropdownStyle,
    autoScrollToSelected = true,
    onPopupScroll,
    triggerProps,
  } = props;

  const [select, setSelect] = useState<string>(value);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  // 暴露内部 ref 给外部
  useImperativeHandle(ref, () => triggerRef.current!, []);

  // 如果是受控模式,使用传入的 popupVisible
  const actualIsOpen = triggerProps?.popupVisible !== undefined ? triggerProps.popupVisible : isOpen;

  const handleSelect = (value: string) => {
    setIsOpen(false);
    triggerProps?.onVisibleChange?.(false);
    setSelect(value);
    onChange?.(value);
  };

  const handleToggleOpen = () => {
    if (disabled) {
      return;
    }
    const newOpen = !actualIsOpen;
    setIsOpen(newOpen);
    triggerProps?.onVisibleChange?.(newOpen);
  };

  useEffect(() => {
    setSelect(value || defaultValue);
  }, [value, defaultValue]);

  // 从 children 中查找选中项的 label
  const selectLabel = useMemo(() => {
    let label = '';
    Children.forEach(children, (child) => {
      if (isValidElement<OptionProps>(child)) {
        if (child.props.value === select) {
          label = child.props.children as string || child.props.label as string || '';
        }
      }
    });
    return label;
  }, [children, select]);

  return (
    <Trigger
      trigger={[]}
      position="bl"
      autoAlignPopupWidth
      {...triggerProps}
      popupVisible={actualIsOpen}
      onVisibleChange={(visible) => {
        if (!visible) {
          setIsOpen(false);
          triggerProps?.onVisibleChange?.(false);
        }
      }}
      popup={() => (
        <Dropdown
          value={select}
          onChange={handleSelect}
          className={dropdownClassName}
          style={dropdownStyle}
          autoScrollToSelected={autoScrollToSelected}
          onScroll={onPopupScroll}
        >
          {children}
        </Dropdown>
      )}
    >
      <div
        ref={triggerRef}
        className={cn(
          'wg-select',
          {
            'wg-select--disabled': disabled,
            'wg-select--open': actualIsOpen,
          },
          className,
        )}
        style={style}
        onClick={handleToggleOpen}
      >
        <span
          className={cn('wg-select__value', {
            'wg-select__value--placeholder': !select,
          })}
        >
          {selectLabel || placeholder}
        </span>
        <div className="wg-select__suffix" style={actualIsOpen ? { transform: `rotate(${180}deg)` } : {}}>
          <IconAngleBottom />
        </div>
      </div>
    </Trigger>
  );
}

const SelectComponent = forwardRef<HTMLDivElement, SelectProps>(Select);

SelectComponent.displayName = 'Select';

// 将 Option 作为静态属性暴露
const SelectWithOption = Object.assign(SelectComponent, {
  Option,
});

export default SelectWithOption;
export type { OptionProps, SelectProps } from './const';
export { Option };
